import { type ExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseTypes"
import { isRichTextEmpty } from "@/modules/ui/components/richText/richTextUtils"
import { ChronoType } from "@modules/chrono/chronoTypes"
import { word } from "@modules/core/utils/i18n"
import { ScoreType, type ScoreResultDraftType, type ScoreSetConfigurationFormikType } from "@modules/scores/scoreTypes"
import * as Yup from "yup"

export type ChronoTypeFormik = ChronoType | "NONE"

export type ExerciseInputKey =
  | "id"
  | "title"
  | "category"
  | "otherCategory"
  | "description"
  | "chrono"
  | "scoreSetConfigurations"
  | "scoringOption"
  | "chronoType"
  | "chronoForTime"
  | "chronoTabata"
  | "chronoEmom"
  | "chronoAmrap"
  | "rpe"

export type ExerciseFormikType = {
  id: string | null
  title: string
  description: string
  category: ExerciseCategory
  otherCategory: string | null
  chrono: ChronoFormik | null
  chronoType: ChronoTypeFormik
  chronoForTime: ForTimeChronoFormik | null
  chronoTabata: TabataChronoFormik | null
  chronoEmom: EmomChronoFormik | null
  chronoAmrap: AmrapChronoFormik | null
  rpe: number | null
  requiredMedia: boolean
  durationInMin: number | null
  scoreSetConfigurations: ScoreSetConfigurationFormikType[]
  scoringOption: ScoreResultDraftType | null
  scoreType: ScoreType
}

export function getExerciseBlockValidationSchema() {
  return Yup.object().shape({
    description: Yup.string()
      .required(word("global.form.required"))
      .test((value, context) => {
        const isValid = !isRichTextEmpty(value)

        if (!isValid) return new Yup.ValidationError(word("global.form.required"), value, context.path)
        return true
      }),
    category: Yup.string().required(word("global.form.required")),
    otherCategory: Yup.string()
      .nullable()
      .max(32, word("form.maximum.characters", { count: 32 })),
    chronoForTime: Yup.object()
      .shape({
        noLimit: Yup.boolean(),
        trainingTime: Yup.number().when("noLimit", (noLimit, schema) => {
          return noLimit[0] ? schema.nullable() : schema.min(1, word("global.form.required"))
        }),
      })
      .nullable(),
    chronoEmom: Yup.object()
      .shape({
        trainingTime: Yup.number().min(1, word("global.form.required")),
      })
      .nullable(),
    chronoTabata: Yup.object()
      .shape({
        trainingTime: Yup.number().min(1, word("global.form.required")),
      })
      .nullable(),
    chronoAmrap: Yup.object()
      .shape({
        trainingTime: Yup.array().of(Yup.number().min(1, word("global.form.required"))),
      })
      .nullable(),
    rpe: Yup.number().nullable().min(1).max(10),
    durationInMin: Yup.number().nullable(),
  })
}

export const convertChronoFormikToChronoDraft = (exerciseFormik: ExerciseFormikType) => {
  switch (exerciseFormik.chronoType) {
    case ChronoType.FOR_TIME:
      return exerciseFormik.chronoForTime
        ? {
            ...exerciseFormik.chronoForTime,
            restTime: exerciseFormik.chronoForTime.restTime,
            trainingTime: exerciseFormik.chronoForTime.trainingTime,
          }
        : undefined
    case ChronoType.AMRAP:
      return exerciseFormik.chronoAmrap
        ? {
            ...exerciseFormik.chronoAmrap,
            restTime: exerciseFormik.chronoAmrap.restTime,
            trainingTime: exerciseFormik.chronoAmrap.trainingTime,
          }
        : undefined
    case ChronoType.EMOM:
      return exerciseFormik.chronoEmom
        ? {
            ...exerciseFormik.chronoEmom,
            restBetweenSetsTime: exerciseFormik.chronoEmom.restBetweenSetsTime,
            trainingTime: exerciseFormik.chronoEmom.trainingTime,
          }
        : undefined
    case ChronoType.TABATA:
      return exerciseFormik.chronoTabata
        ? {
            ...exerciseFormik.chronoTabata,
            restTime: exerciseFormik.chronoTabata.restTime,
            restBetweenSetsTime: exerciseFormik.chronoTabata.restBetweenSetsTime,
            trainingTime: exerciseFormik.chronoTabata.trainingTime,
          }
        : undefined
    default:
      return undefined
  }
}

export type AmrapRoundAndRepScoreType = {
  round: number
  rep: number
}

type BasicProps = {
  type: ChronoType
  trainingTime: number | number[]
  numberOfSets: number
}

export type ChronoFormik = ForTimeChronoFormik | AmrapChronoFormik | EmomChronoFormik | TabataChronoFormik

export type ForTimeChronoFormik = {
  type: ChronoType.FOR_TIME
  restTime: number
  trainingTime: number
  noLimit: boolean
} & BasicProps

export type AmrapChronoFormik = {
  type: ChronoType.AMRAP
  restTime: number[]
  trainingTime: number[]
} & BasicProps

export type EmomChronoFormik = {
  type: ChronoType.EMOM
  numberOfRounds: number
  restBetweenSetsTime: number
  trainingTime: number
} & Omit<BasicProps, "trainingTime">

export type TabataChronoFormik = {
  type: ChronoType.TABATA
  restTime: number
  restBetweenSetsTime: number
  numberOfRounds: number
  trainingTime: number
} & BasicProps
